'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useUserContext } from '@/contexts/UserContext'
import { useForwardGeocodeMutation } from '@/lib/store/api/geocodingApi'
import { isAuthenticatedViaMiddlewareCookie } from '@/lib/isAuthenticatedViaMiddlewareCookie'
import { loadGoogleMapsScript } from '@/components/GoogleMapsService'

export interface Coordinates {
    latitude: number
    longitude: number
}

export interface CustomerCoordinatesResult {
    coordinates: Coordinates | undefined
    // True only while a resolution attempt (forward-geocode or geolocation) is actually
    // in flight. False once one succeeds, or once every fallback has been exhausted
    // without a result (e.g. geolocation denied or unavailable) — callers can use this
    // to stop showing a loading state rather than spinning forever on a permanent denial.
    isResolving: boolean
    // Geocodes a customer-typed address/zip (the drawer's manual override), preferring
    // Nile-Core's own (BAA-covered) forward-geocode endpoint for a signed-in patient —
    // typed input is still PHI once tied to a signed-in patient viewing a medication
    // page — and falling back to the client-side Google Geocoder only when signed out,
    // since the first-party endpoint requires an authenticated session.
    geocodeManualAddress: (address: string) => Promise<Coordinates | undefined>
}

/**
 * Resolves a starting point for the "Compare pharmacies" map/proximity search: the
 * authenticated patient's home address, geocoded through Nile-Core's own (BAA-covered)
 * forward-geocode endpoint — the address never reaches a third party — when signed in
 * and available, otherwise raw browser geolocation, which needs no backend call at all.
 * Only starts resolving once `enabled` is true, so nothing runs — no network call, no
 * geolocation prompt — until the caller (the drawer) is actually open.
 */
export function useCustomerCoordinates(enabled: boolean): CustomerCoordinatesResult {
    const { user } = useUserContext()
    const [triggerForwardGeocode] = useForwardGeocodeMutation()
    const [coordinates, setCoordinates] = useState<Coordinates | undefined>(undefined)
    // Whether an attempt already concluded without a result (geolocation denied/
    // unavailable, or the forward-geocode call failed with no coordinates to show).
    const [hasGivenUp, setHasGivenUp] = useState(false)
    const requestedRef = useRef(false)

    // Derived every render, rather than a useState seeded once from `enabled` at mount:
    // ComparePharmaciesDrawer stays mounted with `enabled` (its `opened` prop) starting
    // false, so a useState(enabled) would freeze at that initial false and only catch up
    // once the effect below runs — one render early, coordinates is still undefined but
    // this reads false, painting an empty list before the loader appears. Deriving it
    // straight from current values is correct on the very next render instead.
    const isResolving = enabled && !coordinates && !hasGivenUp

    useEffect(() => {
        if (!enabled || coordinates || requestedRef.current) return

        // A signed-in patient's profile (and therefore their home address) loads
        // asynchronously and starts out null, indistinguishable from "no home address on
        // file" by that value alone. isAuthenticatedViaMiddlewareCookie() is synchronous,
        // so it tells the two apart: wait for the profile when a session cookie says one
        // is coming, rather than falling back to geolocation and never re-checking.
        if (!user && isAuthenticatedViaMiddlewareCookie()) return

        requestedRef.current = true

        const geolocate = () => {
            if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
                setHasGivenUp(true)
                return
            }
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setCoordinates({ latitude: position.coords.latitude, longitude: position.coords.longitude })
                },
                () => setHasGivenUp(true),
            )
        }

        const homeAddress = user?.homeAddressObject
        if (homeAddress?.zip) {
            const addressString = [homeAddress.line_1, homeAddress.city, homeAddress.state, homeAddress.zip]
                .filter(Boolean)
                .join(', ')
            triggerForwardGeocode({ address: addressString })
                .unwrap()
                .then((result) => {
                    setCoordinates({ latitude: result.latitude, longitude: result.longitude })
                })
                .catch(geolocate)
        } else {
            geolocate()
        }
    }, [enabled, user, coordinates, triggerForwardGeocode])

    const geocodeManualAddress = useCallback(
        async (address: string): Promise<Coordinates | undefined> => {
            // Signed in: route through Nile-Core's forward-geocode endpoint only — no
            // fallback to the client-side Geocoder on failure, since that would still
            // send a signed-in patient's typed location to a non-BAA vendor.
            if (isAuthenticatedViaMiddlewareCookie()) {
                try {
                    const result = await triggerForwardGeocode({ address }).unwrap()
                    return { latitude: result.latitude, longitude: result.longitude }
                } catch {
                    return undefined
                }
            }

            // Signed out: no authenticated endpoint to call, so this is the only option.
            return geocodeAddress(address)
        },
        [triggerForwardGeocode],
    )

    return { coordinates, isResolving, geocodeManualAddress }
}

// Geocodes client-side via the Google Maps JS SDK's own Geocoder. Only used as
// geocodeManualAddress's signed-out fallback above — a signed-in patient's typed
// address is routed through Nile-Core's forward-geocode endpoint instead, since it's
// still PHI once tied to their session. Awaits the Maps script directly
// (loadGoogleMapsScript resolves immediately if it's already loaded, and dedupes with
// any other in-flight load) rather than assuming the drawer's own useGoogleMapsLoaded
// call has finished first.
async function geocodeAddress(address: string): Promise<Coordinates | undefined> {
    await loadGoogleMapsScript()
    return new Promise((resolve) => {
        new google.maps.Geocoder().geocode({ address }, (results, status) => {
            const location = status === 'OK' ? results?.[0]?.geometry?.location : undefined
            resolve(location ? { latitude: location.lat(), longitude: location.lng() } : undefined)
        })
    })
}
